/**
 * AudioStorageService
 *
 * Manages audio URL storage in localStorage with automatic cleanup
 * to prevent QuotaExceededError from accumulated audio URLs.
 */
class AudioStorageService {
  constructor() {
    this.AUDIO_URL_PREFIX = 'audioUrl_';
    this.AUDIO_INDEX_KEY = 'audioUrl_index';
    this.MAX_STORED_URLS = 100; // Keep only the most recent 100 audio URLs
  }

  /**
   * Store an audio URL with automatic cleanup of old entries
   * @param {string} chatMessageId - The chat message ID
   * @param {string} audioUrl - The audio URL (blob URL) to store
   * @returns {boolean} - True if storage succeeded, false otherwise
   */
  setAudioUrl(chatMessageId, audioUrl) {
    const key = `${this.AUDIO_URL_PREFIX}${chatMessageId}`;

    try {
      // Store the audio URL first, then update index only on success
      localStorage.setItem(key, audioUrl);
      this.addToIndex(chatMessageId);
      return true;
    } catch (error) {
      if (this.isQuotaExceededError(error)) {
        console.warn('localStorage quota exceeded, cleaning up old audio URLs...');

        // Clean up old entries and retry
        this.cleanupOldEntries(this.MAX_STORED_URLS / 2); // Remove half to make room

        try {
          localStorage.setItem(key, audioUrl);
          this.addToIndex(chatMessageId); // Add to index after successful retry
          return true;
        } catch (retryError) {
          console.error('Failed to store audio URL even after cleanup:', retryError);
          return false;
        }
      }

      console.error('Error storing audio URL:', error);
      return false;
    }
  }

  /**
   * Get an audio URL from storage
   * @param {string} chatMessageId - The chat message ID
   * @returns {string|null} - The audio URL or null if not found
   */
  getAudioUrl(chatMessageId) {
    const key = `${this.AUDIO_URL_PREFIX}${chatMessageId}`;
    return localStorage.getItem(key);
  }

  /**
   * Remove an audio URL from storage
   * @param {string} chatMessageId - The chat message ID
   */
  removeAudioUrl(chatMessageId) {
    const key = `${this.AUDIO_URL_PREFIX}${chatMessageId}`;
    const audioUrl = localStorage.getItem(key);

    // Revoke the blob URL to free memory
    if (audioUrl && audioUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(audioUrl);
      } catch (e) {
        // Ignore errors when revoking - URL may already be invalid
      }
    }

    localStorage.removeItem(key);
    this.removeFromIndex(chatMessageId);
  }

  /**
   * Add a chat message ID to the index of stored audio URLs
   * @param {string} chatMessageId - The chat message ID to add
   */
  addToIndex(chatMessageId) {
    const index = this.getIndex();

    // Remove if already exists (to move to end as most recent)
    const existingPos = index.indexOf(chatMessageId);
    if (existingPos !== -1) {
      index.splice(existingPos, 1);
    }

    // Add to end (most recent)
    index.push(chatMessageId);

    // If we exceed the limit, remove oldest entries
    while (index.length > this.MAX_STORED_URLS) {
      const oldestId = index.shift();
      this.removeAudioUrlFromStorage(oldestId);
    }

    this.saveIndex(index);
  }

  /**
   * Remove a chat message ID from the index
   * @param {string} chatMessageId - The chat message ID to remove
   */
  removeFromIndex(chatMessageId) {
    const index = this.getIndex();
    const pos = index.indexOf(chatMessageId);
    if (pos !== -1) {
      index.splice(pos, 1);
      this.saveIndex(index);
    }
  }

  /**
   * Get the index of stored audio URL chat message IDs
   * @returns {string[]} - Array of chat message IDs
   */
  getIndex() {
    try {
      const indexJson = localStorage.getItem(this.AUDIO_INDEX_KEY);
      if (indexJson) {
        return JSON.parse(indexJson);
      }
    } catch (e) {
      console.error('Error parsing audio URL index:', e);
    }

    // If no index exists, build it from existing entries (one-time migration)
    const rebuiltIndex = this.buildIndexFromStorage();
    if (rebuiltIndex.length > 0) {
      console.warn('Audio URL index missing, rebuilt from storage entries:', rebuiltIndex.length);
      // Save the rebuilt index to avoid future rebuilds
      try {
        localStorage.setItem(this.AUDIO_INDEX_KEY, JSON.stringify(rebuiltIndex));
      } catch (e) {
        // Ignore save errors during rebuild
      }
    }
    return rebuiltIndex;
  }

  /**
   * Save the index to localStorage
   * @param {string[]} index - Array of chat message IDs
   * @param {boolean} skipCleanup - If true, skip cleanup on quota error to prevent recursion
   */
  saveIndex(index, skipCleanup = false) {
    try {
      localStorage.setItem(this.AUDIO_INDEX_KEY, JSON.stringify(index));
    } catch (error) {
      if (this.isQuotaExceededError(error) && !skipCleanup) {
        // If we can't save the index, try to clean up more aggressively
        // Pass skipCleanup=true to prevent infinite recursion
        this.cleanupOldEntries(Math.floor(index.length / 2), true);
        // Get the updated index after cleanup (cleanupOldEntries already saved it)
        // No need to retry saving here since cleanupOldEntries handles it
      }
    }
  }

  /**
   * Build the index from existing localStorage entries
   * @returns {string[]} - Array of chat message IDs
   */
  buildIndexFromStorage() {
    const index = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.AUDIO_URL_PREFIX) && key !== this.AUDIO_INDEX_KEY) {
        const chatMessageId = key.substring(this.AUDIO_URL_PREFIX.length);
        index.push(chatMessageId);
      }
    }

    return index;
  }

  /**
   * Remove an audio URL from localStorage only (caller manages index separately).
   * Use removeAudioUrl() if you need both storage and index removal.
   * @param {string} chatMessageId - The chat message ID
   */
  removeAudioUrlFromStorage(chatMessageId) {
    const key = `${this.AUDIO_URL_PREFIX}${chatMessageId}`;
    const audioUrl = localStorage.getItem(key);

    // Revoke the blob URL to free memory
    if (audioUrl && audioUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(audioUrl);
      } catch (e) {
        // Ignore errors when revoking
      }
    }

    localStorage.removeItem(key);
  }

  /**
   * Clean up old audio URL entries
   * @param {number} count - Number of entries to remove
   * @param {boolean} skipCleanupOnSave - If true, skip cleanup in saveIndex to prevent recursion
   */
  cleanupOldEntries(count, skipCleanupOnSave = false) {
    const index = this.getIndex();
    // splice() mutates the index array in place, removing the first `count` entries
    // and returning them as `toRemove`. The remaining entries stay in `index`.
    const toRemove = index.splice(0, Math.min(count, index.length));

    for (const chatMessageId of toRemove) {
      this.removeAudioUrlFromStorage(chatMessageId);
    }

    this.saveIndex(index, skipCleanupOnSave);
    console.log(`Cleaned up ${toRemove.length} old audio URL entries`);
  }

  /**
   * Check if an error is a quota exceeded error
   * @param {Error} error - The error to check
   * @returns {boolean} - True if it's a quota exceeded error
   */
  isQuotaExceededError(error) {
    return (
      error instanceof DOMException &&
      (error.code === 22 || // Legacy code
       error.code === 1014 || // Firefox
       error.name === 'QuotaExceededError' ||
       error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
    );
  }

  /**
   * Get the storage key for a chat message ID
   * @param {string} chatMessageId - The chat message ID
   * @returns {string} - The localStorage key
   */
  getStorageKey(chatMessageId) {
    return `${this.AUDIO_URL_PREFIX}${chatMessageId}`;
  }

  /**
   * Extract the chat message ID from a storage key
   * @param {string} audioKey - The localStorage key (format: audioUrl_{chatMessageId})
   * @returns {string} - The chat message ID
   */
  extractChatMessageId(audioKey) {
    if (!audioKey || !audioKey.startsWith(this.AUDIO_URL_PREFIX)) {
      return audioKey; // Return as-is if not a valid audio key
    }
    return audioKey.substring(this.AUDIO_URL_PREFIX.length);
  }

  /**
   * Clear all stored audio URLs
   */
  clearAll() {
    const index = this.getIndex();

    for (const chatMessageId of index) {
      this.removeAudioUrlFromStorage(chatMessageId);
    }

    localStorage.removeItem(this.AUDIO_INDEX_KEY);
    console.log('Cleared all audio URL storage');
  }

  /**
   * Get the current count of stored audio URLs
   * @returns {number} - Number of stored audio URLs
   */
  getStoredCount() {
    return this.getIndex().length;
  }
}

// Export a singleton instance
const audioStorageService = new AudioStorageService();
export default audioStorageService;
